// ABOUTME: Overview page for country configurations filtered by business line.
// ABOUTME: Shows a list of available configuration entities for a given country and business line.
import {Alert, Button, ControlledInput, Icon, PageHeader} from '@hm/fundamental'
import {Content} from '@hconnect/uikit'
import {mdiClose, mdiMagnify} from '@mdi/js'
import React, {useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'

import {useTranslation} from '../../../hooks/useTranslation'
import {useCountryConfigList} from '../CatalogConfigurations/hooks/useCatalogConfig'
import {useEntitiesList} from './hooks/useEntitiesList'
import {useDebouncedValue} from './hooks/useDebouncedValue'
import {toEntitySlug, useCountryName, useShippingTypeFilter, SCOPE_LEVEL_OPTIONS} from './utils'
import {AddConfigurationDialog} from './components/AddConfigurationDialog'
import {BulkImportExportModal} from './components/BulkImportExportModal'
import {EntityCard} from './components/EntityCard'
import {FilterDropdown} from './components/FilterDropdown'
import {ScopeLevelFilterValue, SCOPE_LEVEL_FILTER_ALL} from './types'
import emptyIllustration from '../assets/emptyBackground.svg'

const SEARCH_DEBOUNCE_MS = 300
const SKELETON_COUNT = 3

export const CountryConfigurationsListofEntities = () => {
  const {t} = useTranslation()
  const levelOptions = SCOPE_LEVEL_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(opt.labelKey),
    icon: opt.icon,
    iconClassName: opt.iconClassName,
  }))
  const history = useHistory()
  const {countryId, businessLine} = useParams<{countryId: string; businessLine: string}>()
  const {shippingType, shippingOptions, handleShippingTypeChange} = useShippingTypeFilter(businessLine)

  const [searchText, setSearchText] = useState('')
  const [levelFilter, setLevelFilter] = useState<ScopeLevelFilterValue>(SCOPE_LEVEL_FILTER_ALL)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [addOpen, setAddOpen] = useState(false)

  const debouncedSearch = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS)

  const businessLineLabel = businessLine?.toUpperCase() ?? ''
  const countryName = useCountryName(countryId)
  const basePath = `/country-configurations/${countryId}/${businessLine}`

  const {data: entities, error, isLoading} = useEntitiesList(countryId, businessLine, {
    search: debouncedSearch,
    level: levelFilter,
  })

  const {data: catalogConfigs} = useCountryConfigList()
  const configExists = (catalogConfigs ?? []).some(
    (c) =>
      c.countryCode.toUpperCase() === countryId?.toUpperCase() &&
      c.businessLine.toUpperCase() === businessLineLabel
  )
  const isFiltered = Boolean(debouncedSearch.trim()) || levelFilter !== SCOPE_LEVEL_FILTER_ALL
  const catalogPath = `/catalog-configurations/${countryId?.toUpperCase()}/${businessLineLabel}`

  return (
    <Content className="p-6">
      <PageHeader
        data-cy="page-country-configurations-overview"
        className="pl-0 pr-0 md:pl-0 md:pr-0 [&_h1]:!text-white"
        aside={
          <div className="flex items-center gap-3">
            {catalogConfigs !== undefined && !configExists && (
              <Button
                variant="primary"
                type="button"
                onClick={() => setAddOpen(true)}
                data-cy="add-configuration-button"
              >
                {t('countryConfigurations.overview.addButton')}
              </Button>
            )}
            <Button
              variant="secondary"
              type="button"
              onClick={() => setBulkOpen(true)}
              data-cy="bulk-import-export-button"
            >
              {t('countryConfigurations.importExport.bulk.openButton')}
            </Button>
            {shippingOptions && shippingType && (
              <FilterDropdown
                value={shippingType}
                onChange={handleShippingTypeChange}
                options={shippingOptions}
                label={t('countryConfigurations.overview.filterShipping')}
                size="regular"
                data-cy="shipping-filter-dropdown"
              />
            )}
            <FilterDropdown
              value={levelFilter}
              onChange={(v) => setLevelFilter(v as ScopeLevelFilterValue)}
              options={levelOptions}
              label={t('countryConfigurations.overview.filterLevel')}
              allValue={SCOPE_LEVEL_FILTER_ALL}
              size="regular"
              data-cy="level-filter-dropdown"
            />
            <div className="relative w-[320px]" data-cy="entity-search-input">
              <ControlledInput
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                label={t('countryConfigurations.overview.searchPlaceholder')}
                variant="regular"
                className={`w-full [&>div]:!bg-container_background [&>div]:focus-within:!border-border_highlight_10 [&>div]:!pr-0`}
              />
              <div className="absolute right-0 bottom-0 h-[48px] pr-3 flex items-center shrink-0">
                <Icon path={mdiMagnify} size="regular" className="text-foreground" />
                {searchText && (
                  <>
                    <div className="w-px h-full bg-border_highlight_10 mx-3" />
                    <Button
                      variant="tertiary"
                      type="button"
                      onClick={() => setSearchText('')}
                      aria-label="Clear search"
                      icon={mdiClose}
                      size="extraSmall"
                      className="!h-auto !p-0 !min-w-0 !rounded-none !text-foreground hover:!bg-transparent active:!bg-transparent"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        }
      >
        {t('countryConfigurations.overview.title', {
          businessLine: businessLineLabel,
          countryName
        })}
      </PageHeader>

      {bulkOpen && (
        <BulkImportExportModal
          open
          onClose={() => setBulkOpen(false)}
          entities={entities ?? []}
          countryId={countryId}
          businessLine={businessLine}
        />
      )}
      {addOpen && (
        <AddConfigurationDialog
          open
          onClose={() => setAddOpen(false)}
          defaultCountry={countryId}
          defaultBusinessLine={businessLine}
        />
      )}
      <div className="px-9 pt-4 pb-6 min-h-[400px] w-full max-w-5xl mx-auto">

        {!isLoading && error && (
          <Alert variant="error" data-cy="entities-error">
            {String(error)}
          </Alert>
        )}

        <div className="flex flex-col gap-1">
          {isLoading ? (
            Array.from({length: SKELETON_COUNT}).map((_, i) => (
              <div
                key={i}
                className="rounded-lg border border-border bg-container_background p-4 animate-pulse"
                data-cy={`entity-card-skeleton-${i}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex flex-col gap-1">
                    <div className="h-5 w-48 bg-border rounded" />
                    <div className="h-4 w-80 bg-border rounded" />
                  </div>
                  <div className="flex gap-2">
                    <div className="h-6 w-6 rounded-full bg-border" />
                    <div className="h-6 w-6 rounded-full bg-border" />
                  </div>
                </div>
              </div>
            ))
          ) : (entities ?? []).length === 0 ? (
            <div
              className="flex flex-col items-center justify-center py-16 gap-6"
              data-cy="entities-empty-state"
            >
              <img src={emptyIllustration} alt="" className="w-[240px] h-[154px]" />
              <div className="flex flex-col items-center gap-2">
                <p className="text-lg font-semibold text-white">{t('countryConfigurations.overview.noResultsTitle')}</p>
                <p className="text-base text-white/70">{t('countryConfigurations.overview.noResultsHint')}</p>
              </div>
              {configExists && !isFiltered && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => history.push(catalogPath)}
                  data-cy="open-catalog-button"
                >
                  {t('countryConfigurations.overview.openCatalogButton')}
                </Button>
              )}
            </div>
          ) : (
            (entities ?? []).map((entity) => (
              <EntityCard
                key={entity.id}
                data-cy={`entity-card-${entity.id}`}
                title={entity.name}
                description={entity.description}
                applicableLevels={entity.applicableLevels}
                configuredFields={[]}
                searchHighlight={debouncedSearch}
                onClick={() => history.push(`${basePath}/${toEntitySlug(entity.id)}`)}
              />
            ))
          )}
        </div>
      </div>
    </Content>
  )
}

